import React, { useEffect, useState } from 'react';
import { MainLayout } from '../components/layouts/MainLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { DataTable } from '../components/ui/data-table';
import { Badge } from '../components/ui/badge';
import { logsAPI } from '../lib/firestore';
import { useToast } from '../components/ui/use-toast';
import type { AuditLog } from '../types';
import { ScrollText } from 'lucide-react';

const ACTION_COLORS: Record<string, string> = {
  send_offboarding_email: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 border-blue-300 dark:border-blue-700',
  send_reminder_email: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 border-yellow-300 dark:border-yellow-700',
  offboarding_confirmation: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 border-green-300 dark:border-green-700',
  login: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200 border-indigo-300 dark:border-indigo-700',
  create: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200 border-emerald-300 dark:border-emerald-700',
  update: 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 border-amber-300 dark:border-amber-700',
  delete: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 border-red-300 dark:border-red-700',
};

const getActionColor = (action: string) => {
  const match = Object.entries(ACTION_COLORS).find(([key]) => action.includes(key));
  return match ? match[1] : 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-700';
};

const formatDate = (timestamp: string) => {
  try {
    const date = new Date(timestamp);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return timestamp;
  }
};

const formatMetadata = (metadata?: Record<string, any>) => {
  if (!metadata) return '-';
  const entries = Object.entries(metadata).slice(0, 3);
  return entries.map(([key, value]) => `${key}: ${value}`).join(', ');
};

export const ActivityLogsPage: React.FC = () => {
  const { toast } = useToast();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const data = await logsAPI.getRecent(500);
      setLogs(data);
    } catch (error) {
      console.error('Error loading logs:', error);
      toast({
        title: 'Error',
        description: 'Failed to load activity logs',
        className: 'bg-red-50 border-red-200',
      });
    } finally {
      setLoading(false);
    }
  };

  // Extract unique actions and resources for filters
  const uniqueActions = Array.from(new Set(logs.map((l) => l.action))).sort();
  const uniqueResources = Array.from(new Set(logs.map((l) => l.resource))).sort();

  return (
    <MainLayout>
      <div className="space-y-6 min-w-0">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <ScrollText className="h-8 w-8" />
            Activity Logs
          </h1>
          <p className="text-muted-foreground mt-1">
            View recent activity and audit trail
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-muted-foreground">Loading logs...</div>
          </div>
        ) : (
          <Card className="overflow-hidden">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ScrollText className="h-5 w-5" />
                Recent Activity ({logs.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="min-w-0">
              <DataTable
                data={logs}
                columns={[
                  {
                    key: 'timestamp',
                    label: 'Date',
                    sortable: true,
                    render: (log) => (
                      <span className="text-sm">{formatDate(log.timestamp)}</span>
                    ),
                  },
                  {
                    key: 'userEmail',
                    label: 'User',
                    sortable: true,
                    render: (log) => (
                      <span className="text-sm font-medium">{log.userEmail || '-'}</span>
                    ),
                  },
                  {
                    key: 'action',
                    label: 'Action',
                    sortable: true,
                    render: (log) => (
                      <Badge variant="outline" className={getActionColor(log.action)}>
                        {log.action}
                      </Badge>
                    ),
                  },
                  {
                    key: 'resource',
                    label: 'Resource',
                    sortable: true,
                    render: (log) => (
                      <span className="text-sm">
                        {log.resource}
                        {log.resourceId && (
                          <span className="text-muted-foreground ml-1 text-xs">
                            ({log.resourceId.slice(0, 8)}...)
                          </span>
                        )}
                      </span>
                    ),
                  },
                  {
                    key: 'metadata',
                    label: 'Details',
                    render: (log) => (
                      <span className="text-xs text-muted-foreground max-w-xs truncate block">
                        {formatMetadata(log.metadata)}
                      </span>
                    ),
                  },
                ]}
                searchKeys={['userEmail', 'action', 'resourceId', 'resource']}
                filters={[
                  {
                    key: 'action',
                    label: 'Action',
                    options: uniqueActions.map((a) => ({ value: a, label: a })),
                  },
                  {
                    key: 'resource',
                    label: 'Resource',
                    options: uniqueResources.map((r) => ({ value: r, label: r })),
                  },
                ]}
                emptyMessage="No activity logs found"
                pageSize={25}
              />
            </CardContent>
          </Card>
        )}
      </div>
    </MainLayout>
  );
};
